/**
 * Puts the gardener's chosen theme on the page: a class on <html> that the
 * Tailwind theme variants (grow:, fieldnotes:, dark:) key on.
 *
 * The theme follows the account's preferences (UserConfigProvider); the
 * context also offers setTheme for a preview that has not been saved, which
 * the next change of the saved preference replaces. Saving a theme is
 * UserConfigProvider's (updateUserConfig, savePreferences), not this file's;
 * utils/themeUtils applyTheme sets the same classes from there, and keeps
 * the theme on the device for the next first paint.
 *
 * The class names are the Theme values; 'dark' also turns on Tailwind's
 * dark: variant. Old class names (garden, almanac) are removed each time.
 *
 * Mounted once by App inside UserConfigProvider, around the signed-in pages.
 *
 * @module ThemeProvider
 */
import { useState, useEffect, useMemo, ReactNode } from 'react';
import { Theme, ThemeContext } from './ThemeContext';
import { normaliseTheme } from '@/shared/utils/themeUtils';
import { useUserConfig } from './userConfigStore';
import { debugLog } from '@/config/config';

/** 
 * Props for the ThemeProvider component
 * @interface
 */
interface ThemeProviderProps {
  /** Child components that will have access to the theme context */
  children: ReactNode;
}

/**
 * Provider component for theme context that manages application-wide theming
 * 
 * Handles both the application theme system and Tailwind's dark mode:
 * - Holds the theme, starting from (and following) the saved preference
 * - Applies theme classes to document root
 * - Handles Tailwind dark mode class separately
 * 
 * @example
 * ```tsx
 * <ThemeProvider>
 *   <App />
 * </ThemeProvider>
 * ```
 * 
 * @param props - Component properties
 * @param props.children - Child components
 * @returns Theme provider component
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ 
  children
}) => {
  const { userConfig } = useUserConfig();
  // A brand-new account has no saved theme yet — the server sends null — and
  // classList.add(null) put a class called "null" on the page. Grow is the
  // answer until they choose; so is anything saved under a retired name.
  const chosen: Theme = normaliseTheme(userConfig.preferences.theme);
  const [theme, setTheme] = useState<Theme>(chosen);

  // Update theme when user preferences change
  useEffect(() => {
    setTheme(chosen);
  }, [chosen]);

  useEffect(() => {
    debugLog('Theme', 'ThemeProvider effect:', {
      theme,
      userTheme: userConfig.preferences.theme,
      classList: document.documentElement.classList.toString()
    });

    // Remove all theme classes
    document.documentElement.classList.remove('grow', 'light', 'dark', 'fieldnotes', 'garden', 'almanac');
    
    // Add the theme class
    document.documentElement.classList.add(theme);
    
    // Handle dark mode for Tailwind
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme, userConfig.preferences.theme]);

  // One object per theme, not per render: every useTheme() reader re-rendered
  // whenever anything above this provider did.
  const value = useMemo(() => ({ theme, setTheme }), [theme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}; 